import { useCallback } from "react";
import { processFilesInBatches } from "../classes/utils/FileUtils";
import { useFileValidation } from "./useFileValidation";

export const useFileManagement = options => {
  const {
    uploadedFiles,
    setUploadedFiles,
    allowedFileTypes,
    setIsImporting,
    setImportProgress,
  } = options;

  const { validateFiles, resetGroupWarnings } = useFileValidation();

  const addFiles = useCallback(
    async files => {
      try {
        setIsImporting(true); 
        const processedFiles = await processFilesInBatches(
          files,
          allowedFileTypes,
          20,
          progress => setImportProgress(progress)
        );

        // Validate and filter rejected files
        const rejectedKeys = validateFiles(processedFiles, allowedFileTypes);
        const validFiles = rejectedKeys.size > 0
          ? processedFiles.filter(f => {
              const key = f.instanceId
                ? `inst_${f.instanceId}`
                : f.file?.size !== undefined && f.file?.lastModified
                  ? `file_${f.name}_${f.file.size}_${f.file.lastModified}`
                  : `name_${f.name}_${f.seriesId || ""}_${f.studyId || ""}`;
              return !rejectedKeys.has(key);
            })
          : processedFiles;

        setUploadedFiles(prevFiles => [...prevFiles, ...validFiles]);
      } catch (error) {
        alert("Error importing files. Please retry.");
      } finally {
        setIsImporting(false);
        setImportProgress(0);
      }
    },
    [allowedFileTypes, setIsImporting, setImportProgress, setUploadedFiles, validateFiles]
  );

  const removeFile = useCallback(
    groupToRemove => {
  
      setUploadedFiles(prevFiles => {
        // Create a unique key for each file to properly identify it
        const getFileKey = (file) => {
          // Use instanceId if available (unique for DICOM files)
          if (file.instanceId) {
            return `inst_${file.instanceId}`;
          }
          // Use file system properties (size + lastModified + name)
          if (file.file && file.file.size !== undefined && file.file.lastModified) {
            return `file_${file.name}_${file.file.size}_${file.file.lastModified}`;
          }
          // Fallback to name + seriesId + studyId combination
          return `name_${file.name}_${file.seriesId || ''}_${file.studyId || ''}`;
        };
        
        // Create a Set of keys for files that should be removed
        const filesToRemoveKeys = new Set(
          (groupToRemove.files || []).map(getFileKey)
        );
        
        // Filter: keep only files whose keys are NOT in the removal set
        const remainingFiles = prevFiles.filter(file => {
          const fileKey = getFileKey(file);
          const shouldRemove = filesToRemoveKeys.has(fileKey);
          
          
          return !shouldRemove;
        });
        
        return remainingFiles;
      });

      // Clear file inputs
      const fileInput = document.getElementById("fileInput");
      if (fileInput) fileInput.value = "";
      const directoryInput = document.getElementById("directoryInput");
      if (directoryInput) directoryInput.value = "";
    },
    [setUploadedFiles]
  );

  return { addFiles, removeFile, resetGroupWarnings };
};
